import { normalizeLiveFramesDetailed, type NormalizedLiveFrame } from './normalizeLiveFrames';
import type { CanFrame } from '../canlog/types';

export type DeviceStatus = Record<string, unknown>;

const REQUEST_TIMEOUT_MS = 5500;

export class DeviceRequestError extends Error {
  url: string;
  status?: number;

  constructor(message: string, url: string, status?: number) {
    super(message);
    this.name = 'DeviceRequestError';
    this.url = url;
    this.status = status;
  }
}

export type LiveFrameFetchResult = {
  frames: NormalizedLiveFrame[];
  endpointUrl: string;
  httpStatus: number;
  contentType: string;
  rawFrameCount: number;
  parsedFrameCount: number;
  invalidFrameCount: number;
};

export function normalizeDeviceBaseUrl(value: string) {
  const trimmed = value.trim().replace(/\/+$/, '');
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `http://${trimmed}`;
}

export function getDeviceStatusUrl(baseUrl: string) {
  return baseUrl ? `${baseUrl}/api/status` : '';
}

export function getDeviceLiveUrl(baseUrl: string) {
  return baseUrl ? `${baseUrl}/api/live` : '';
}

export function getDeviceFrameEndpointUrls(baseUrl: string) {
  return baseUrl ? [`${baseUrl}/api/live`, `${baseUrl}/api/frames`, `${baseUrl}/api/can`] : [];
}

export function getDeviceLocalUiUrl(baseUrl: string) {
  return baseUrl ? `${baseUrl}/` : '';
}

function getHostname(value: string) {
  try {
    return new URL(value).hostname.toLowerCase();
  } catch {
    return '';
  }
}

export function isPrivateOrLocalHost(value: string) {
  const host = getHostname(value);
  if (!host) return false;
  if (host === 'localhost' || host.endsWith('.local')) return true;
  if (/^10\./.test(host)) return true;
  if (/^192\.168\./.test(host)) return true;
  const match172 = host.match(/^172\.(\d+)\./);
  if (match172) {
    const secondOctet = Number.parseInt(match172[1], 10);
    return secondOctet >= 16 && secondOctet <= 31;
  }
  if (/^169\.254\./.test(host)) return true;
  return false;
}

export function isLikelyMixedContentBlocked(baseUrl: string, pageProtocol = window.location.protocol) {
  return pageProtocol === 'https:' && baseUrl.toLowerCase().startsWith('http://') && isPrivateOrLocalHost(baseUrl);
}

export function explainDeviceError(error: unknown, mixedContentLikely = false) {
  if (mixedContentLikely) {
    return 'Device API may be reachable, but this hosted HTTPS page cannot fetch HTTP local devices due to browser mixed-content protection.';
  }

  const message = error instanceof Error ? error.message : String(error);
  if (message.includes('timeout') || message.includes('aborted')) {
    return 'Device API could not be reached from this browser. The SmartCAN device API timed out. Make sure your device is powered and on the same Wi-Fi network.';
  }
  if (message.includes('Failed to fetch') || message.includes('NetworkError') || message.includes('Load failed')) {
    return 'Device API could not be reached from this browser. If the status link opens in a new tab, your device is online but the browser blocked SmartCAN Web because CORS/local-network access is not enabled.';
  }
  return 'Device API could not be reached from this browser. If the status link opens in a new tab, your device is online but the browser blocked SmartCAN Web because CORS/local-network access is not enabled.';
}

async function fetchWithTimeout(url: string) {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(new Error('timeout')), REQUEST_TIMEOUT_MS);
  try {
    let response: Response;
    try {
      response = await fetch(url, {
        cache: 'no-store',
        signal: controller.signal
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Network request failed';
      throw new DeviceRequestError(message, url);
    }
    if (!response.ok) throw new DeviceRequestError(`HTTP ${response.status}`, url, response.status);
    return response;
  } finally {
    window.clearTimeout(timer);
  }
}

export async function fetchDeviceStatus(baseUrl: string): Promise<DeviceStatus> {
  const response = await fetchWithTimeout(getDeviceStatusUrl(baseUrl));
  return await response.json() as DeviceStatus;
}

export async function fetchLiveFramesDetailed(baseUrl: string, startIndex: number, previousFrame?: CanFrame): Promise<LiveFrameFetchResult> {
  const endpointUrls = getDeviceFrameEndpointUrls(baseUrl);
  let lastError: unknown = null;

  for (const endpointUrl of endpointUrls) {
    try {
      const response = await fetchWithTimeout(endpointUrl);
      const contentType = response.headers.get('content-type') ?? '';
      const payload = contentType.includes('application/json') ? await response.json() : await response.text();
      const normalized = normalizeLiveFramesDetailed(payload, startIndex, previousFrame);
      return {
        ...normalized,
        endpointUrl,
        httpStatus: response.status,
        contentType
      };
    } catch (error) {
      lastError = error;
      if (!(error instanceof DeviceRequestError) || ![404, 405].includes(error.status ?? 0)) break;
    }
  }

  throw lastError instanceof Error ? lastError : new Error('Frame endpoint request failed.');
}

export async function fetchLiveFrames(baseUrl: string, startIndex: number, previousFrame?: CanFrame): Promise<NormalizedLiveFrame[]> {
  const result = await fetchLiveFramesDetailed(baseUrl, startIndex, previousFrame);
  return result.frames;
}
